import { computed, onScopeDispose, ref, shallowRef } from 'vue'
import { ConversionPool } from '@/core/pool'
import type { WorkerLike } from '@/core/protocol'
import type { EnginePreference, Job } from '@/core/types'
import { markdownToHtml, formatHtml } from '@/core/render'

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `job-${Math.random().toString(36).slice(2)}`
}

function makeWorker(): WorkerLike {
  return new Worker(new URL('../workers/convert.worker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as WorkerLike
}

/**
 * Owns the file queue and drives conversions through the worker pool.
 *
 * Rendering is derived, never stored: Markdown is the only thing an engine
 * produces, and the HTML and Preview panes are computed from it on demand. That
 * keeps the three views incapable of drifting apart, and avoids re-rendering
 * every queued document when only one is on screen.
 */
export function useConversions(engine = ref<EnginePreference>('auto')) {
  const jobs = ref<Job[]>([])
  const selectedId = ref<string | null>(null)
  const pool = shallowRef(new ConversionPool(makeWorker))

  const selected = computed(() => jobs.value.find((job) => job.id === selectedId.value) ?? null)

  const markdown = computed(() => selected.value?.result?.markdown ?? '')
  const html = computed(() => markdownToHtml(markdown.value))
  const htmlSource = computed(() => formatHtml(html.value))

  const busy = computed(() =>
    jobs.value.some((job) => job.status === 'converting' || job.status === 'queued'),
  )
  const doneCount = computed(() => jobs.value.filter((job) => job.status === 'done').length)

  async function add(files: readonly File[]): Promise<void> {
    const created: Job[] = files.map((file) => ({
      id: newId(),
      name: file.name,
      size: file.size,
      status: 'queued',
    }))
    jobs.value = [...jobs.value, ...created]
    selectedId.value ??= created[0]?.id ?? null

    await Promise.all(created.map((job, index) => convert(job, files[index]!)))
  }

  async function convert(job: Job, file: File): Promise<void> {
    patch(job.id, { status: 'converting' })

    // Read on the main thread — FileReader is async and cheap. Only the parse
    // is expensive, and that is what the worker is for.
    let bytes: ArrayBuffer
    try {
      bytes = await file.arrayBuffer()
    } catch {
      patch(job.id, {
        status: 'error',
        error: { code: 'unknown', message: 'The file could not be read from disk.' },
      })
      return
    }

    const outcome = await pool.value.convert(job.id, job.name, bytes, engine.value)

    if (outcome.ok) {
      patch(job.id, { status: 'done', result: outcome.result, format: outcome.result.format })
    } else {
      patch(job.id, { status: 'error', error: outcome.error })
    }

    // Land on something useful rather than an empty pane.
    if (selected.value?.status !== 'done') {
      selectedId.value = jobs.value.find((j) => j.status === 'done')?.id ?? selectedId.value
    }
  }

  function patch(id: string, changes: Partial<Job>): void {
    jobs.value = jobs.value.map((job) => (job.id === id ? { ...job, ...changes } : job))
  }

  function select(id: string): void {
    selectedId.value = id
  }

  function remove(id: string): void {
    jobs.value = jobs.value.filter((job) => job.id !== id)
    if (selectedId.value === id) selectedId.value = jobs.value[0]?.id ?? null
  }

  function clear(): void {
    jobs.value = []
    selectedId.value = null
  }

  onScopeDispose(() => pool.value.dispose())

  return {
    jobs,
    selected,
    selectedId,
    markdown,
    html,
    htmlSource,
    busy,
    doneCount,
    add,
    select,
    remove,
    clear,
  }
}
